import {
  Checkbox,
  Combobox,
  ComboboxGroup,
  ComboboxOption,
  Select,
  TextField
} from '@deque/cauldron-react';
import React, {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ComboboxValue } from '@deque/cauldron-react/lib/components/Combobox/ComboboxOption';
import type { TemplateFormFields } from '~/app/types';
import { useGlobalToast } from '~/common/contexts/globalToast';
import type {
  IssueType,
  ProjectsAndIssueTypes
} from '~/common/utils/integrations-client/jira/getProjectsAndIssueTypes';
import { defaultTemplateValues } from '../../TemplateWizardForm/TemplateWizardForm';
import styles from './TemplateInformation.css';

interface ProjectComboboxOption {
  key: string;
  label: string;
  value: string;
}

interface ProjectComboboxData {
  projectOptions: ProjectComboboxOption[];
  projectDisplayValueById: Record<string, string>;
  projectIdByDisplayValue: Record<string, string>;
  recentProjectIds: Set<string>;
}

const renderProjectOption = (option: ProjectComboboxOption) => (
  <ComboboxOption value={option.value} key={option.key}>
    {option.label}
  </ComboboxOption>
);

const buildProjectComboboxData = (
  projects: ProjectsAndIssueTypes[]
): ProjectComboboxData => {
  const projectOptions: ProjectComboboxOption[] = projects.map(project => ({
    key: project.id,
    label: `${project.name} (${project.key})`,
    value: `${project.name} (${project.key})`
  }));

  return {
    projectOptions,
    projectDisplayValueById: Object.fromEntries(
      projectOptions.map(option => [option.key, option.value])
    ),
    projectIdByDisplayValue: Object.fromEntries(
      projectOptions.map(option => [option.value, option.key])
    ),
    recentProjectIds: new Set<string>(
      projects.filter(project => project.isRecent).map(project => project.id)
    )
  };
};

export interface TemplateInformationProps {
  projects: ProjectsAndIssueTypes[];
  getIssueTypesByProjectId: (id: string | null) => IssueType[] | undefined;
  setIsStepDataError: Dispatch<SetStateAction<boolean>>;
}

const TemplateInformation = ({
  projects,
  getIssueTypesByProjectId,
  setIsStepDataError
}: TemplateInformationProps) => {
  const { t } = useTranslation();
  const {
    control,
    getValues,
    formState: { errors },
    reset,
    resetField
  } = useFormContext<TemplateFormFields>();
  const { setContents } = useGlobalToast();

  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [projectComboboxInputValue, setProjectComboboxInputValue] =
    useState('');
  const [initialProjectId, setInitialProjectId] = useState('');

  const {
    projectOptions,
    projectDisplayValueById,
    projectIdByDisplayValue,
    recentProjectIds
  } = useMemo(() => buildProjectComboboxData(projects), [projects]);

  useEffect(() => {
    const projectFieldValue = getValues('project');

    if (projectFieldValue) {
      setInitialProjectId(projectFieldValue);
      setContents(
        t(
          'Changing Project or Issue Type will result in loss of Field and Impact Mappings.'
        ),
        'caution'
      );
      setSelectedProjectId(projectFieldValue);
    }
    // Leave dependency array empty to only run on mount
  }, []);

  useEffect(() => {
    if (!initialProjectId) {
      return;
    }
    const persistedProjectId = projects.find(
      p => p.id === initialProjectId
    )?.id;
    setSelectedProjectId(persistedProjectId || '');
  }, [projects, initialProjectId]);

  useEffect(() => {
    // Keep the combobox text in sync after a selection or when we hydrate an
    // existing template from its persisted project ID.
    setProjectComboboxInputValue(
      projectDisplayValueById[selectedProjectId] || ''
    );
  }, [selectedProjectId, projectDisplayValueById]);

  const resetNextSteps = () => {
    if (initialProjectId) {
      const { impactMapping, fieldMapping, summaryPrefix } =
        defaultTemplateValues;
      const values = getValues();

      reset({
        ...values,
        summaryPrefix,
        fieldMapping,
        impactMapping
      });
    }
  };

  const onProjectSelect = (
    value: ComboboxValue,
    onChange: (e: string) => void
  ) => {
    // Cauldron Combobox gives us back the selected display text,
    // so we map it to the stored project ID before updating form state.
    const selectedDisplayValue = value as string | undefined;
    const selectedId =
      projectIdByDisplayValue[selectedDisplayValue ?? ''] ?? '';
    setSelectedProjectId(selectedId);

    if (selectedProjectId !== selectedId) {
      resetField('issueType', { defaultValue: '' });
      setIsStepDataError(false);
    }

    onChange(selectedId);
  };

  const recentProjectOptions = useMemo(
    () =>
      projectOptions
        .filter(option => recentProjectIds.has(option.key))
        .map(renderProjectOption),
    [projectOptions, recentProjectIds]
  );

  const otherProjectOptions = useMemo(
    () =>
      projectOptions
        .filter(option => !recentProjectIds.has(option.key))
        .map(renderProjectOption),
    [projectOptions, recentProjectIds]
  );

  const renderProjectComboboxOptions = () => {
    if (recentProjectOptions.length === 0) {
      return otherProjectOptions.length > 0 ? otherProjectOptions : null;
    }
    return (
      <>
        {recentProjectOptions.length > 0 && (
          <ComboboxGroup
            label={t('Recent projects')}
            data-testid="recent-project-group"
          >
            {recentProjectOptions}
          </ComboboxGroup>
        )}
        {otherProjectOptions.length > 0 && (
          <ComboboxGroup
            label={t('Other projects')}
            data-testid="other-project-group"
          >
            {otherProjectOptions}
          </ComboboxGroup>
        )}
      </>
    );
  };

  return (
    <div className={styles.templateInformationContainer}>
      <Controller
        name="templateName"
        control={control}
        render={({ field: { ref, ...field } }) => (
          <TextField
            {...field}
            label={t('Template Name')}
            error={errors.templateName?.message}
            required
            fieldRef={ref}
          />
        )}
      />
      <Controller
        name="project"
        control={control}
        render={({ field: { ref, onChange, ...field } }) => (
          <div className={styles.projectContainer}>
            <Combobox
              className={styles.projectCombobox}
              {...field}
              ref={ref}
              value={projectComboboxInputValue}
              // Typing only updates the visible filter text; the form value
              // changes only after an option is explicitly selected.
              onChange={event =>
                setProjectComboboxInputValue(event.target.value)
              }
              onSelectionChange={event =>
                onProjectSelect(event.value, onChange)
              }
              label={t('Project')}
              placeholder={t('Select Project')}
              error={errors.project?.message}
              required
            >
              {renderProjectComboboxOptions()}
            </Combobox>
          </div>
        )}
      />
      <Controller
        name="issueType"
        control={control}
        render={({ field }) => (
          <div className={styles.issueTypeContainer}>
            <Select
              {...field}
              label={t('Issue Type')}
              error={errors.issueType?.message}
              disabled={!selectedProjectId}
              onChange={event => {
                field.onChange(event);
                resetNextSteps();
                setIsStepDataError(false);
              }}
              required
            >
              <option key="" value="">
                {t('Select Issue Type')}
              </option>
              <>
                {getIssueTypesByProjectId(selectedProjectId)?.map(issueType => (
                  <option key={issueType.id} value={issueType.id}>
                    {issueType.name}
                  </option>
                ))}
              </>
            </Select>
            {!selectedProjectId && (
              <p className={styles.issueTypeHelperText}>
                {t('Select a project to see available issue types.')}
              </p>
            )}
          </div>
        )}
      />
      <Controller
        name="attachScreenshot"
        control={control}
        render={({ field: { value, ...field } }) => (
          <Checkbox
            {...field}
            label={t('Enable Axe Issue screenshots as attachments')}
            required
            checked={value}
            id="attach-screenshot-checkbox"
          />
        )}
      />
    </div>
  );
};

export default TemplateInformation;
